import {
  ExportOutlined,
  LogoutOutlined,
  MenuOutlined,
  QuestionCircleOutlined,
  UnlockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Dropdown, Flex, Layout, Modal, message, theme, Drawer } from "antd";
import { useState } from "react";
import CompanyLogo from "assets/aktos-logo-1004x923.png";
import { resetStateAction } from "common/redux/resetStateAction";
import { useFetchMeQuery, useLogoutMutation } from "features/auth/authAPI";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useWhiteLabel } from "providers/whiteLabelProvider";
import AppMenu from "./menu";

const { Header } = Layout;

const StyledLogo = styled("img")`
  margin-left: 8px;
  cursor: pointer;
  height: 48px;
  max-width: 220px;
  object-fit: contain;
  @media (max-width: 768px) {
    margin-left: 0px;
    max-width: 160px;
  }
`;

const StyledCaret = styled.div`
  margin-left: 6px;
  margin-right: 16px;
  font-size: 16px;
  margin-top: -4px;
  transform: rotate(180deg);
`;

const StyledName = styled.span`
  text-transform: capitalize;
`;

const StyledAvatar = styled(Avatar)`
  vertical-align: middle;
  margin-right: 4px;
`;

const StyledMenu = styled("span")`
  user-select: none;
  cursor: pointer;
`;

const StyledProfileMenu = styled(StyledMenu)`
  height: 100%;
  display: flex;
  align-items: center;
`;

const StyledHeader = styled(Header)`
  z-index: 10;
  position: fixed;
  width: 100%;
  border-bottom: 1px solid #d9d9d9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &&& {
    padding-inline: 0;
  }
`;

const StyledUserActions = styled.div`
  float: right;
  @media only screen and (max-width: 320px) {
    background: #fff;
    color: #404fc7;
  }
  &:hover {
    color: gray;
    .ant-avatar {
      background: lightgray;
    }
  }
`;

const StyledMenuOutlined = styled(MenuOutlined)`
  margin-right: 16px;
  font-size: 18px;
  color: #000;
  cursor: pointer;
  @media (min-width: 768px) {
    display: none;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-close {
    color: #fff;

    &:hover {
      color: rgba(255, 255, 255, 0.8);
    }
  }
`;

function DashboardHeader() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: currentUser } = useFetchMeQuery();
  const [logout] = useLogoutMutation();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const { isWhiteLabeled, logo } = useWhiteLabel();

  const onLogout = () => {
    const logoutModal = Modal.confirm({
      title: "Logout",
      content: "Are you sure you want to log out?",
      icon: <LogoutOutlined />,
      onCancel: () => {
        /** No need to define body */
      },
      onOk: async () => {
        const result = await logout();
        if ("data" in result) {
          await dispatch(resetStateAction());
          message.success("Logged out successfully.", 2);
          if (result.data?.ssoLogoutUrl?.startsWith("https://")) {
            window.location.href = result.data.ssoLogoutUrl;
          }
        }
      },
    });
  };

  const onChangePassword = () => {
    navigate("/change-password");
  };

  const items = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "View Profile",
      actionHandler: () => {
        navigate("/profile");
      },
    },
    {
      key: "changePassword",
      icon: <UnlockOutlined />,
      label: "Change Password",
      actionHandler: onChangePassword,
    },
    {
      key: "contactUs",
      icon: <QuestionCircleOutlined />,
      label: "Help",
      actionHandler: () => {
        navigate("/contact");
      },
    },
    {
      key: "logout",
      icon: <ExportOutlined />,
      label: "Log Out",
      actionHandler: onLogout,
    },
  ];

  const onDropdownClick = ({ item, key, keyPath, domEvent }) => {
    // This prevents navigation to invoice detail when clicking on a dropdown item
    domEvent.preventDefault();
    domEvent.stopPropagation();

    // Call the action handler defined in the items dictionary
    item.props.actionHandler();
  };

  return (
    <StyledHeader
      style={{
        padding: 0,
        background: colorBgContainer,
      }}
    >
      <StyledLogo src={isWhiteLabeled ? logo : CompanyLogo} onClick={() => navigate("/accounts")} />
      <Flex gap={16}>
        <StyledUserActions>
          {/* <Divider type="vertical" /> */}
          <Dropdown
            menu={{
              // @ts-ignore
              items,
              onClick: (clickHandlerProp) => onDropdownClick(clickHandlerProp),
            }}
            trigger={["click"]}
          >
            <StyledProfileMenu>
              <StyledAvatar shape="circle" size="small" icon={<UserOutlined />} />
              {currentUser?.name && <StyledName>{currentUser.name.toLowerCase()}</StyledName>}
              <StyledCaret>^</StyledCaret>
            </StyledProfileMenu>
          </Dropdown>
        </StyledUserActions>
        <StyledMenuOutlined
          onClick={() => setIsDrawerOpen(true)}
          style={{ fontSize: 18, color: "#000", cursor: "pointer" }}
        />
      </Flex>
      <StyledDrawer
        title={null}
        placement="left"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        width={240}
        styles={{
          body: {
            padding: 0,
            background: "#001529",
          },
          header: {
            background: "#001529",
            color: "#fff",
          },
        }}
      >
        <AppMenu inDrawer onMenuItemClick={() => setIsDrawerOpen(false)} />
      </StyledDrawer>
    </StyledHeader>
  );
}

export default DashboardHeader;
